import { offerPresentationId } from "@/lib/analytics-offer-milestones";
import {
  offerPlacementRuntimePolicy,
  type PlacementContract,
  type RuntimeOfferPlacementKey,
} from "@/lib/offer-placement-runtime.generated";

export type PlacementContractInput<K extends RuntimeOfferPlacementKey> = Omit<
  PlacementContract<K>,
  "presentationId" | "intent"
> & {
  presentationId?: string;
  presentationScope?: string;
  /** Only consulted when the placement policy allows more than one intent. */
  intent?: string;
};

/** Resolve and validate every attribution field before an offer can render. */
export function resolvePlacementContract<K extends RuntimeOfferPlacementKey>(
  input: PlacementContractInput<K>
): PlacementContract<K> {
  const { presentationId, presentationScope, ...contract } = input;
  const policy = offerPlacementRuntimePolicy(contract.placement);
  const sourcePath = normalizePath(contract.sourcePath);
  assertAllowed(policy.runtimeOwners, contract.runtimeOwner, "runtime owner", contract.placement);
  const intent = resolveGovernedIntent(policy.intents, contract.intent, contract.placement);
  assertAllowed(policy.offerPlans, contract.planId, "plan", contract.placement);
  assertAllowed(policy.actionModes, contract.actionMode, "action mode", contract.placement);
  assertSourcePath(policy.sourcePathTemplates, sourcePath, contract.placement);
  return {
    ...contract,
    intent,
    sourcePath,
    presentationId:
      presentationId ??
      offerPresentationId({
        actionMode: contract.actionMode,
        planId: contract.planId,
        placement: contract.placement,
        presentationScope,
        sourcePath,
      }),
  } as PlacementContract<K>;
}

/**
 * The placement policy OWNS the contract intent (rule 40: the offer matches the
 * page, declared in value_surface_placements.yaml). 42 of 43 placements allow
 * exactly one intent, so the policy value is stamped regardless of what the
 * caller derived — callers speak the SEO-intent taxonomy ("generic",
 * "chart_report"), which is a DIFFERENT vocabulary from the placement policy's
 * alignment intents ("career"). Asserting caller intent against policy intent
 * conflated the two ontologies and silently suppressed offer_shown on every
 * mismatch (the 2026-08-02 /dasamsa "does not allow intent generic" incident —
 * real seekers' impressions went unrecorded while the rendered bridge kept
 * working). Only a multi-intent placement needs the caller to disambiguate.
 */
function resolveGovernedIntent(
  allowed: readonly string[],
  requested: string | undefined,
  placement: string
): string {
  if (allowed.length === 1) return allowed[0];
  if (!allowed.length) {
    if (requested) return requested;
    throw new Error(`Offer placement ${placement} has no governed intent and none was provided`);
  }
  if (requested && allowed.includes(requested)) return requested;
  throw new Error(
    `Offer placement ${placement} allows intents [${allowed.join(", ")}] and needs the caller to pick one; got ${requested ?? "none"}`
  );
}

function assertAllowed(
  allowed: readonly string[],
  actual: string,
  field: string,
  placement: string
) {
  if (allowed.length && !allowed.includes(actual)) {
    throw new Error(`Offer placement ${placement} does not allow ${field} ${actual}`);
  }
}

function assertSourcePath(allowed: readonly string[], actual: string, placement: string) {
  if (!allowed.some((template) => pathMatches(template, actual))) {
    throw new Error(`Offer placement ${placement} does not govern source path ${actual}`);
  }
}

function pathMatches(template: string, sourcePath: string): boolean {
  const normalizedTemplate = normalizePath(template);
  const normalizedSource = normalizePath(sourcePath);
  const escaped = normalizedTemplate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replace(/\\\{[^/]+\\\}/g, "[^/]+")}$`).test(normalizedSource);
}

function normalizePath(path: string): string {
  const clean = path.split(/[?#]/, 1)[0] || "/";
  const leading = clean.startsWith("/") ? clean : `/${clean}`;
  return leading === "/" ? leading : leading.replace(/\/+$/, "");
}
